import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: string;
    type: 'positive' | 'negative' | 'neutral';
  };
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  sx?: SxProps<Theme>;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  trend,
  color = 'primary',
  sx = {},
}) => {
  const getTrendColor = () => {
    switch (trend?.type) {
      case 'positive': return 'success';
      case 'negative': return 'error';
      default: return 'info';
    }
  };

  const getIconColor = () => {
    switch (color) {
      case 'success': return 'success.main';
      case 'warning': return 'warning.main';
      case 'error': return 'error.main';
      case 'info': return 'info.main';
      default: return 'primary.main';
    }
  };

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        boxShadow: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-4px)',
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ color: getIconColor(), fontSize: '2rem' }}>
            {icon}
          </Box>
          {trend && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: `${getTrendColor()}.light`,
                color: `${getTrendColor()}.main`,
                fontSize: '0.75rem',
                fontWeight: 'semibold',
                textTransform: 'capitalize',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {trend.value}
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 'medium',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: getIconColor(),
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};
